import { asAuthUserId } from '@/lib/ids';
import type { ScoreCriteria, AIRecommendation } from '@/lib/scoring/types';
import type { StartupProfile } from '@/lib/state/profile-store';
import {
  DataRoomDocument,
  InvestorMatch,
  InvestorProfile,
  DealflowStartup,
  StartupUpdate,
  PortfolioCompany,
  VerificationRequest,
  UserInfo,
  InvestorBadge,
  InvestorCommitment,
  ExternalCommitment,
} from './inverio-store';
import { startupEnrichments, mockDataRoomDocuments } from './mock-enrichments';

// Re-export mockDataRoomDocuments for mock-data-provider (per-startup keyed map)
export { mockDataRoomDocuments };

// Mock User Info for Investors (personal info separate from investor profile)
//
// `id` IS LOAD-BEARING, and its absence made demo mode look broken. The "Try
// Demo Mode" login sets one of these as the current user, and essentially every
// investor-side hook is gated on that id — `useSubmissions` opens with
// `if (!investorId) return []`, before it ever reaches its mock branch. Without
// an id the demo showed an empty submissions inbox (0 total, 0 scored) while
// advertising sample data, and the same short-circuit emptied everything else
// keyed on the auth user.
//
// The record was already keyed BY the id; the value just never carried it.
// `asAuthUserId(...)` rather than `as AuthUserId`: ARCH-09 permits the
// sanctioned constructor and forbids the bare cast, because a mixed-up id space
// produces no error at all — only an empty screen.
export const mockInvestorUsers: Record<string, UserInfo> = {
  i1: { id: asAuthUserId('i1'), name: 'Sarah Chen', email: 'sarah.chen@horizonvc.com', role: 'Managing Partner' },
  i2: { id: asAuthUserId('i2'), name: 'Marcus Williams', email: 'marcus@greencapital.com', role: 'Partner' },
  i3: { id: asAuthUserId('i3'), name: 'Elena Rodriguez', email: 'elena@fintechangels.com', role: 'General Partner' },
  i4: { id: asAuthUserId('i4'), name: 'David Park', email: 'david.park@healthinvest.com', role: 'Angel Investor' },
};

// Mock Startup Profiles
export const mockStartups: StartupProfile[] = [
  {
    id: 's1',
    name: 'TechFlow AI',
    tagline: 'AI-powered workflow automation for SMBs',
    description: 'TechFlow AI uses proprietary machine learning models to automate repetitive business processes. Our platform integrates with existing tools and learns from user behavior to suggest optimizations, saving teams 20+ hours per week.',
    industry: 'Enterprise SaaS',
    stage: 'seed',
    fundingTarget: 2000000,
    minimumTicket: 50000,
    syndicatesAllowed: true,
    location: 'San Francisco, CA',
    foundedYear: 2023,
    teamSize: 8,
    website: 'techflow.ai',
    linkedIn: 'https://linkedin.com/company/techflow-ai',

    // Problem/Solution (from questionnaire)
    problem: 'SMBs waste 20+ hours per week on repetitive manual tasks that could be automated. Existing solutions are too expensive or complex for small teams.',
    solution: 'AI-powered workflow automation that learns from your team patterns and automates tasks with minimal setup. 3x more accurate than competitors at detecting automation opportunities.',
    targetCustomer: 'Operations managers at SMBs (50-500 employees) in professional services, with a focus on accounting, legal, and consulting firms.',

    // Traction
    monthlyRevenue: 18000,
    userCount: 1000,
    growthRate: 15,

    // Market
    marketSize: 45000000000, // $45B
    competitors: ['Zapier', 'Make', 'Monday.com'],

    // Fundraising
    isActivelyRaising: true,

    // Badges
    isSerialFounder: true,
    previousVentures: ['DataSync (acqui-hired by Salesforce 2021)'],

    updates: [
      {
        id: 'upd_s1_1',
        type: 'milestone',
        title: 'Reached 1,000 Active Users',
        summary: 'We\'re thrilled to announce that TechFlow AI has crossed the 1,000 active user milestone, representing 3x growth in the past quarter.',
        date: '2025-01-20',
        createdBy: 'Alex Chen',
        visibility: 'public',
      },
      {
        id: 'upd_s1_2',
        type: 'product',
        title: 'New AI Assistant Feature Launched',
        summary: 'Introducing our new AI-powered workflow assistant that automatically suggests optimizations based on your team\'s patterns.',
        externalLink: 'https://techflow.ai/blog/ai-assistant',
        date: '2025-01-15',
        createdBy: 'Alex Chen',
        visibility: 'public',
      },
      {
        id: 'upd_s1_3',
        type: 'team',
        title: 'Welcome Our New CTO',
        summary: 'Excited to welcome Sarah Johnson, former Google engineer, as our new CTO. She brings 15 years of experience in AI/ML.',
        date: '2025-01-10',
        createdBy: 'Alex Chen',
        visibility: 'data_room',
      },
    ],
  },
  {
    id: 's2',
    name: 'GreenHarvest',
    tagline: 'Sustainable urban farming technology',
    industry: 'AgriTech',
    stage: 'pre-seed',
    fundingTarget: 500000,
    minimumTicket: 10000,
    syndicatesAllowed: true,
    location: 'Austin, TX',
    foundedYear: 2024,
    teamSize: 4,
    updates: [
      {
        id: 'upd_s2_1',
        type: 'traction',
        title: 'First Commercial Pilot Success',
        summary: 'Our urban farm pilot with Whole Foods resulted in 40% higher yield compared to traditional vertical farming methods.',
        date: '2025-01-18',
        createdBy: 'Maria Santos',
        visibility: 'public',
      },
    ],
  },
  {
    id: 's3',
    name: 'FinLedger',
    tagline: 'Blockchain-based financial reconciliation',
    industry: 'FinTech',
    stage: 'seed',
    fundingTarget: 3000000,
    minimumTicket: 100000,
    syndicatesAllowed: false,
    location: 'New York, NY',
    foundedYear: 2022,
    teamSize: 12,
    updates: [
      {
        id: 'upd_s3_1',
        type: 'funding',
        title: 'Seed Round 60% Committed',
        summary: 'We\'ve secured commitments for 60% of our $3M seed round from leading fintech investors.',
        date: '2025-01-22',
        createdBy: 'James Wong',
        visibility: 'investors_only',
      },
      {
        id: 'upd_s3_2',
        type: 'announcement',
        title: 'Featured in TechCrunch',
        summary: 'FinLedger was featured in TechCrunch\'s "10 Fintech Startups to Watch in 2025" list.',
        externalLink: 'https://techcrunch.com/fintech-2025',
        date: '2025-01-12',
        createdBy: 'James Wong',
        visibility: 'public',
      },
    ],
  },
  {
    id: 's4',
    name: 'HealthSync',
    tagline: 'Remote patient monitoring platform',
    industry: 'HealthTech',
    stage: 'series-a',
    fundingTarget: 8000000,
    minimumTicket: 250000,
    syndicatesAllowed: true,
    location: 'Boston, MA',
    foundedYear: 2021,
    teamSize: 25,
    updates: [
      {
        id: 'upd_s4_1',
        type: 'milestone',
        title: 'FDA Clearance Received',
        summary: 'HealthSync has received FDA 510(k) clearance for our remote patient monitoring device, a major milestone for our Series A fundraise.',
        date: '2025-01-24',
        createdBy: 'Dr. Emily Parker',
        visibility: 'public',
      },
      {
        id: 'upd_s4_2',
        type: 'traction',
        title: '$500K ARR Milestone',
        summary: 'We\'ve reached $500K ARR with 15 healthcare provider partnerships across 8 states.',
        date: '2025-01-08',
        createdBy: 'Dr. Emily Parker',
        visibility: 'data_room',
      },
    ],
  },
];

// Mock Investor Profiles (personal info is in mockInvestorUsers)
export const mockInvestors: InvestorProfile[] = [
  {
    id: 'i1',
    firm: 'Horizon Ventures',
    investmentFocus: ['Enterprise SaaS', 'AI/ML', 'Developer Tools'],
    stagePreference: ['seed', 'series-a'],
    ticketSize: { min: 250000, max: 2000000 },
    location: 'San Francisco, CA',
    portfolioCount: 24,
  },
  {
    id: 'i2',
    firm: 'Green Capital',
    investmentFocus: ['CleanTech', 'AgriTech', 'Sustainability'],
    stagePreference: ['pre-seed', 'seed'],
    ticketSize: { min: 100000, max: 500000 },
    location: 'Austin, TX',
    portfolioCount: 18,
  },
  {
    id: 'i3',
    firm: 'FinTech Angels',
    investmentFocus: ['FinTech', 'Blockchain', 'Payments'],
    stagePreference: ['seed', 'series-a'],
    ticketSize: { min: 500000, max: 3000000 },
    location: 'New York, NY',
    portfolioCount: 31,
  },
  {
    id: 'i4',
    investmentFocus: ['HealthTech', 'BioTech', 'Digital Health'],
    stagePreference: ['seed', 'series-a'],
    ticketSize: { min: 200000, max: 1500000 },
    location: 'Boston, MA',
    portfolioCount: 12,
  },
];

// Enhanced mock investor profile with all new fields
export const mockEnhancedInvestorProfile: InvestorProfile = {
  id: 'i1',
  firm: 'Horizon Ventures',
  investmentFocus: ['Enterprise SaaS', 'AI/ML', 'Developer Tools'],
  stagePreference: ['seed', 'series-a'],
  ticketSize: { min: 250000, max: 2000000 },
  location: 'San Francisco, CA',
  portfolioCount: 24,
  linkedInVerified: true,
  linkedInUrl: 'https://linkedin.com/in/sarahchen',
  linkedInVerifiedAt: '2024-06-15',
  yearsInvesting: 12,
  seniorityLevel: 'veteran',

  // Enhanced bio
  bio: {
    headline: 'Former Stripe PM | 50+ angel investments | Enterprise SaaS focus',
    bio: 'I spent 8 years building products at Stripe, Plaid, and Square before transitioning to full-time investing. I\'m passionate about backing technical founders solving hard problems in developer tools and enterprise infrastructure. I write first checks and lead seed rounds, typically as the first institutional investor.',
    previousRoles: [
      { title: 'VP Product', company: 'Stripe', years: '2016-2022' },
      { title: 'Senior PM', company: 'Plaid', years: '2014-2016' },
      { title: 'Product Manager', company: 'Square', years: '2012-2014' },
    ],
  },

  // Investment thesis
  thesis: {
    thesisStatement: 'I invest in technical founders building the infrastructure layer of tomorrow\'s businesses. My best investments have been in companies where the founders have deep domain expertise and are solving problems they\'ve personally experienced.',
    sweetSpot: 'B2B SaaS and developer tools at seed stage, $500K-$1.5M checks',
    sectors: ['Enterprise SaaS', 'AI/ML', 'Developer Tools', 'Fintech Infrastructure'],
    stageCheckSizes: [
      { stage: 'Pre-seed', min: 50000, max: 150000 },
      { stage: 'Seed', min: 250000, max: 1000000 },
      { stage: 'Series A', min: 500000, max: 2000000 },
    ],
    businessModelPreferences: ['SaaS', 'Usage-based', 'Platform/Marketplace'],
    teamPreferences: ['Technical founders', 'Second-time founders', 'Ex-FAANG operators'],
    dealBreakers: ['No consumer apps', 'No hardware-first', 'No pre-product at seed'],
  },

  // Value-add
  valueAdd: {
    expertiseAreas: ['Go-to-market strategy', 'Enterprise sales', 'Product strategy', 'Fundraising'],
    description: 'I\'m most helpful with product positioning, pricing strategy, and enterprise sales playbooks. I host monthly portfolio dinners and quarterly founder retreats. My network includes 200+ enterprise CTOs and VP Engineering contacts.',
    officeHours: true,
    boardExperience: 8,
  },

  // Engagement metrics
  engagement: {
    responseRate: 94,
    avgResponseTime: '24-48 hours',
    introRequestsAccepted: 156,
    activePlatformUser: true,
  },

  // DD activity
  ddActivity: {
    dataRoomRequests30d: 12,
    ddInProgress: 4,
    avgDecisionDays: 21,
    conversionRate: 18,
  },

  // Investment patterns
  patterns: {
    avgCheckSize: 750000,
    followOnRate: 65,
    avgTimeToTermSheet: '2-3 weeks',
    leadVsFollow: 'lead',
    syndicateParticipation: true,
  },

  // Social links
  socialLinks: {
    twitter: '@sarahchenvc',
    website: 'sarahchen.vc',
    calendly: 'calendly.com/sarahchen',
    substack: 'sarahchen.substack.com',
  },

  // Badges
  // BADGES ARE DERIVED, NOT DECLARED. A literal array lived here claiming
  // "50+ investments", "80% lead rounds", "94% response rate", "4.8/5 rating"
  // and "8 board seats" for this demo investor — none of it backed by a column,
  // a table or a writer, so production showed no badges at all while demo mode
  // advertised credentials that could not be earned. They now come from
  // lib/investor/badges.ts, computed from the same rows that justify them, so
  // mock and production agree about what an investor has actually done.

  // Investment history
  investmentHistory: [
    {
      id: 'inv1',
      companyName: 'DataPulse',
      industry: 'Enterprise SaaS',
      stage: 'seed',
      investmentType: 'direct',
      securityType: 'safe',
      date: '2022-06-15',
      investmentAmount: 500000,
      valuationAtInvestment: 12000000,
      currentValuation: 45000000,
      ownership: 4.2,
      status: 'active',
      investorRole: 'board-member',
      source: 'manual',
    },
    {
      id: 'inv2',
      companyName: 'CloudSecure',
      industry: 'Cybersecurity',
      stage: 'seed',
      investmentType: 'direct',
      securityType: 'equity',
      date: '2021-03-20',
      investmentAmount: 250000,
      valuationAtInvestment: 8000000,
      currentValuation: 62000000,
      ownership: 2.8,
      status: 'active',
      investorRole: 'board-observer',
      source: 'manual',
    },
    {
      id: 'inv3',
      companyName: 'APIFlow',
      industry: 'Developer Tools',
      stage: 'pre-seed',
      investmentType: 'syndicate',
      securityType: 'safe',
      date: '2023-09-10',
      investmentAmount: 100000,
      valuationAtInvestment: 6000000,
      currentValuation: 15000000,
      ownership: 1.5,
      status: 'active',
      investorRole: 'advisor',
      source: 'manual',
    },
    {
      id: 'inv4',
      companyName: 'Paystream',
      industry: 'Fintech',
      stage: 'seed',
      investmentType: 'direct',
      securityType: 'equity',
      date: '2020-11-01',
      investmentAmount: 350000,
      status: 'exited',
      investorRole: 'none',
      source: 'manual',
    },
  ],
};

/**
 * 13 Investment Readiness Dimensions
 * Based on Inverio Methodology v2.0
 *
 * Each dimension has sub-criteria that contribute to the overall dimension score.
 * Weights are applied based on funding stage (see methodology.ts).
 */
export const defaultScoreCriteria: ScoreCriteria[] = [
  // Team - Founding team quality and completeness
  { id: 'dim-team', name: 'Team', dimension: 'team', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Product - Product stage, quality, and differentiation
  { id: 'dim-product', name: 'Product', dimension: 'product', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Market - Market size, growth, and dynamics
  { id: 'dim-market', name: 'Market', dimension: 'market', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Traction - Evidence of product-market fit
  { id: 'dim-traction', name: 'Traction', dimension: 'traction', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Defensibility - Moat and competitive advantages
  { id: 'dim-defensibility', name: 'Defensibility', dimension: 'defensibility', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Competition - Competitive landscape
  { id: 'dim-competition', name: 'Competition', dimension: 'competition', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Business Model - Revenue model and unit economics
  { id: 'dim-business-model', name: 'Business Model', dimension: 'business-model', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Timing - Why now and market tailwinds
  { id: 'dim-timing', name: 'Timing', dimension: 'timing', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Vision & Story - Compelling narrative
  { id: 'dim-vision-story', name: 'Vision & Story', dimension: 'vision-story', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // GTM Execution - Go-to-market strategy
  { id: 'dim-gtm-execution', name: 'GTM Execution', dimension: 'gtm-execution', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Financial Health - Runway and capital efficiency
  { id: 'dim-financial-health', name: 'Financial Health', dimension: 'financial-health', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Execution Velocity - Speed and milestone achievement
  { id: 'dim-execution-velocity', name: 'Execution Velocity', dimension: 'execution-velocity', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Scalability - Technical, operational, team scalability
  { id: 'dim-scalability', name: 'Scalability', dimension: 'scalability', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Deal Attractiveness - How attractive the deal is to investors
  { id: 'dim-deal-attractiveness', name: 'Deal Attractiveness', dimension: 'deal-attractiveness', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },

  // Social Proof - Endorsements, advisors, notable backers
  { id: 'dim-social-proof', name: 'Social Proof', dimension: 'social-proof', score: 0, maxScore: 100, feedback: '', riskLevel: 'none', evidenceLevel: 'missing', verified: false },
];

/**
 * Generate sample scored criteria using 15-dimension v3 methodology
 * Represents a seed-stage startup (like TechFlow AI) with realistic scores
 * Evidence tiers: verified (1.0x) | partially-verified (0.75x) | unverified (0.5x)
 */
export const getSampleScoredCriteria = (): ScoreCriteria[] => [
  // 1. TEAM (Seed weight: 16%)
  {
    id: 'dim-team-scored',
    name: 'Team',
    dimension: 'team',
    score: 78,
    maxScore: 100,
    feedback: 'Strong founding team with relevant backgrounds. Missing dedicated sales lead.',
    recommendation: 'Prioritize hiring a VP of Sales or experienced sales lead within the next 2-3 months. Consider bringing on a fractional sales advisor in the interim to help build the sales playbook.',
    riskLevel: 'low',
    evidenceLevel: 'verified',
    verified: true,
    verificationNote: 'LinkedIn profiles, CVs, and org chart verified',
    subScores: [
      { id: 'team-founder-experience', name: 'Founder Experience', score: 80, maxScore: 100, feedback: 'CTO ex-Google ML engineer, CEO has previous startup (acqui-hired)' },
      { id: 'team-completeness', name: 'Team Completeness', score: 65, maxScore: 100, feedback: 'Core tech team strong, need sales/BD lead' },
      { id: 'team-domain-expertise', name: 'Domain Expertise', score: 90, maxScore: 100, feedback: 'Deep AI/ML expertise, lived the problem at previous companies' },
      { id: 'team-coachability', name: 'Coachability & Culture', score: 75, maxScore: 100, feedback: 'Generally receptive to feedback, collaborative team' },
    ],
  },

  // 2. PRODUCT (Seed weight: 12%)
  {
    id: 'dim-product-scored',
    name: 'Product',
    dimension: 'product',
    score: 72,
    maxScore: 100,
    feedback: 'Solid MVP with paying customers. Technical differentiation through proprietary ML models.',
    recommendation: 'Focus on polishing the admin panel UX based on user feedback. Document your technical differentiation more clearly in marketing materials to strengthen competitive positioning.',
    riskLevel: 'low',
    evidenceLevel: 'partially-verified',
    verified: true,
    verificationNote: 'Product demo completed, technical docs reviewed',
    subScores: [
      { id: 'product-stage', name: 'Development Stage', score: 70, maxScore: 100, feedback: 'MVP in production with 12 paying customers' },
      { id: 'product-quality', name: 'Product Quality & UX', score: 68, maxScore: 100, feedback: 'Good core UX, some rough edges in admin panel' },
      { id: 'product-differentiation', name: 'Technical Differentiation', score: 82, maxScore: 100, feedback: 'Proprietary ML models outperform competitors by 3x on accuracy' },
      { id: 'product-roadmap', name: 'Product Roadmap', score: 70, maxScore: 100, feedback: 'Clear 12-month roadmap, aligned with customer requests' },
    ],
  },

  // 3. MARKET (Seed weight: 10%)
  {
    id: 'dim-market-scored',
    name: 'Market',
    dimension: 'market',
    score: 82,
    maxScore: 100,
    feedback: 'Large addressable market with strong tailwinds. AI adoption accelerating.',
    recommendation: 'Continue to refine your ICP and consider publishing thought leadership content to establish authority in the SMB AI automation space.',
    riskLevel: 'low',
    evidenceLevel: 'verified',
    verified: true,
    verificationNote: 'Third-party market research report validated',
    subScores: [
      { id: 'market-size', name: 'Market Size (TAM/SAM/SOM)', score: 85, maxScore: 100, feedback: '$45B TAM, $5B SAM for SMB workflow automation' },
      { id: 'market-growth', name: 'Market Growth Rate', score: 90, maxScore: 100, feedback: '28% YoY growth in AI/automation tools' },
      { id: 'market-dynamics', name: 'Market Dynamics', score: 75, maxScore: 100, feedback: 'Clear buyer (Ops leaders), 30-60 day sales cycle' },
      { id: 'market-positioning', name: 'Market Positioning', score: 78, maxScore: 100, feedback: 'Good positioning for SMBs, differentiated from enterprise solutions' },
    ],
  },

  // 4. TRACTION (Seed weight: 14%)
  {
    id: 'dim-traction-scored',
    name: 'Traction',
    dimension: 'traction',
    score: 65,
    maxScore: 100,
    feedback: 'Growing revenue with strong retention. Need to accelerate customer acquisition.',
    recommendation: 'Focus on increasing MoM growth to 20%+. Implement a referral program to leverage your high NPS. Target 20+ paying customers before next fundraise.',
    riskLevel: 'medium',
    evidenceLevel: 'verified',
    verified: true,
    verificationNote: 'Stripe dashboard and bank statements verified',
    subScores: [
      { id: 'traction-revenue', name: 'Revenue / MRR', score: 60, maxScore: 100, feedback: '$18K MRR, 15% MoM growth' },
      { id: 'traction-users', name: 'User/Customer Growth', score: 65, maxScore: 100, feedback: '12 paying customers, adding 2-3/month' },
      { id: 'traction-engagement', name: 'Engagement & Retention', score: 75, maxScore: 100, feedback: '92% logo retention, strong daily active usage' },
      { id: 'traction-pmf', name: 'Product-Market Fit Signals', score: 62, maxScore: 100, feedback: 'Early PMF signals: NPS 42, some word-of-mouth' },
    ],
  },

  // 5. DEFENSIBILITY (Seed weight: 8%)
  {
    id: 'dim-defensibility-scored',
    name: 'Defensibility',
    dimension: 'defensibility',
    score: 58,
    maxScore: 100,
    feedback: 'Some IP protection but limited network effects. Building switching costs.',
    recommendation: 'Accelerate patent filing process and explore data network effects by enabling anonymized benchmarking across customers. Deepen integrations to increase switching costs.',
    riskLevel: 'medium',
    evidenceLevel: 'partially-verified',
    verified: false,
    verificationNote: 'Patent applications uploaded, pending verification',
    subScores: [
      { id: 'defensibility-ip', name: 'Intellectual Property', score: 65, maxScore: 100, feedback: '2 patents pending on ML approach' },
      { id: 'defensibility-network', name: 'Network Effects', score: 40, maxScore: 100, feedback: 'Data advantage building, no network effects yet' },
      { id: 'defensibility-switching', name: 'Switching Costs', score: 60, maxScore: 100, feedback: 'Moderate switching costs, workflow dependencies' },
      { id: 'defensibility-brand', name: 'Brand & Reputation', score: 55, maxScore: 100, feedback: 'Building brand awareness in AI/automation space' },
    ],
  },

  // 6. COMPETITION (Seed weight: 4%)
  {
    id: 'dim-competition-scored',
    name: 'Competition',
    dimension: 'competition',
    score: 68,
    maxScore: 100,
    feedback: 'Competitive market but clear differentiation vs incumbents targeting enterprises.',
    recommendation: 'Create detailed competitive battle cards for the sales team. Upload a formal competitive analysis document to strengthen this score and verify your positioning claims.',
    riskLevel: 'medium',
    evidenceLevel: 'unverified',
    verified: false,
    verificationNote: 'Competitive analysis not independently verified',
    subScores: [
      { id: 'competition-landscape', name: 'Competitive Landscape', score: 65, maxScore: 100, feedback: 'Crowded but focused on different segments' },
      { id: 'competition-differentiation', name: 'Differentiation vs Competitors', score: 75, maxScore: 100, feedback: 'Better accuracy, SMB focus, simpler setup' },
      { id: 'competition-response', name: 'Competitive Response Risk', score: 62, maxScore: 100, feedback: 'Enterprise players unlikely to move downmarket soon' },
    ],
  },

  // 7. BUSINESS MODEL (Seed weight: 10%)
  {
    id: 'dim-business-model-scored',
    name: 'Business Model',
    dimension: 'business-model',
    score: 62,
    maxScore: 100,
    feedback: 'SaaS model proving out. Unit economics improving but need more data.',
    recommendation: 'Complete the cohort analysis to prove LTV:CAC. Push annual contracts to improve payback period. Target LTV:CAC of 3x+ before Series A.',
    riskLevel: 'medium',
    evidenceLevel: 'unverified',
    verified: false,
    verificationNote: 'Cohort analysis in progress, preliminary data reviewed',
    subScores: [
      { id: 'bm-revenue-model', name: 'Revenue Model Clarity', score: 75, maxScore: 100, feedback: 'Clear SaaS tiers, testing annual plans' },
      { id: 'bm-unit-economics', name: 'Unit Economics', score: 55, maxScore: 100, feedback: 'LTV:CAC ~2.5x estimated, payback ~14 months' },
      { id: 'bm-profitability', name: 'Path to Profitability', score: 60, maxScore: 100, feedback: '72% gross margin, 36-month path to profitability' },
      { id: 'bm-scalability', name: 'Business Model Scalability', score: 70, maxScore: 100, feedback: 'Good scalability, low COGS per customer' },
    ],
  },

  // 8. TIMING (Seed weight: 5%)
  {
    id: 'dim-timing-scored',
    name: 'Timing',
    dimension: 'timing',
    score: 88,
    maxScore: 100,
    feedback: 'Excellent timing. AI/automation adoption accelerating post-2023.',
    recommendation: 'Capitalize on the AI momentum by moving quickly. Consider launching a thought leadership blog or podcast to position founders as AI experts.',
    riskLevel: 'none',
    evidenceLevel: 'verified',
    verified: true,
    verificationNote: 'Industry reports and trends data confirm timing thesis',
    subScores: [
      { id: 'timing-why-now', name: 'Why Now', score: 90, maxScore: 100, feedback: 'ChatGPT wave making AI accessible, SMBs now interested' },
      { id: 'timing-tailwinds', name: 'Market Tailwinds', score: 85, maxScore: 100, feedback: 'AI adoption, labor costs rising, remote work trends' },
    ],
  },

  // 9. VISION & STORY (Seed weight: 5%)
  {
    id: 'dim-vision-story-scored',
    name: 'Vision & Story',
    dimension: 'vision-story',
    score: 75,
    maxScore: 100,
    feedback: 'Compelling vision for AI-powered operations. Pitch could be stronger.',
    recommendation: 'Work with a pitch coach to sharpen competitive framing. Add more concrete customer success stories to the narrative to make it more compelling.',
    riskLevel: 'low',
    evidenceLevel: 'partially-verified',
    verified: true,
    verificationNote: 'Pitch deck reviewed, investor call observed',
    subScores: [
      { id: 'vision-clarity', name: 'Vision Clarity', score: 80, maxScore: 100, feedback: 'Clear vision: "AI ops co-pilot for every SMB"' },
      { id: 'vision-storytelling', name: 'Storytelling & Pitch', score: 70, maxScore: 100, feedback: 'Good pitch but could improve competitive framing' },
    ],
  },

  // 10. GTM EXECUTION (Seed weight: 6%)
  {
    id: 'dim-gtm-execution-scored',
    name: 'GTM Execution',
    dimension: 'gtm-execution',
    score: 55,
    maxScore: 100,
    feedback: 'Founder-led sales working but need to build scalable channels.',
    recommendation: 'Document your GTM strategy and upload to data room. Identify 1-2 scalable acquisition channels and double down. Consider hiring or contracting a growth marketer.',
    riskLevel: 'high',
    evidenceLevel: 'unverified',
    verified: false,
    verificationNote: 'GTM plan not fully documented',
    subScores: [
      { id: 'gtm-strategy', name: 'GTM Strategy', score: 50, maxScore: 100, feedback: 'Testing content + outbound, no clear winner yet' },
      { id: 'gtm-sales', name: 'Sales Capability', score: 55, maxScore: 100, feedback: 'Founder-led sales, closing deals but not scalable' },
      { id: 'gtm-marketing', name: 'Marketing & Brand', score: 60, maxScore: 100, feedback: 'Basic marketing, building LinkedIn presence' },
    ],
  },

  // 11. FINANCIAL HEALTH (Seed weight: 3%)
  {
    id: 'dim-financial-health-scored',
    name: 'Financial Health',
    dimension: 'financial-health',
    score: 70,
    maxScore: 100,
    feedback: 'Good runway, controlled burn. Financial hygiene improving.',
    recommendation: 'Maintain current burn discipline. Consider extending runway to 18+ months before next raise. Set up formal board reporting if not already in place.',
    riskLevel: 'low',
    evidenceLevel: 'verified',
    verified: true,
    verificationNote: 'Bank statements and financial model verified',
    subScores: [
      { id: 'fin-runway', name: 'Runway & Burn', score: 75, maxScore: 100, feedback: '14 months runway at current burn ($45K/mo)' },
      { id: 'fin-capital-efficiency', name: 'Capital Efficiency', score: 60, maxScore: 100, feedback: '$0.36 ARR per $1 raised (seed stage typical)' },
      { id: 'fin-hygiene', name: 'Financial Hygiene', score: 72, maxScore: 100, feedback: 'Good bookkeeping, monthly reporting in place' },
    ],
  },

  // 12. EXECUTION VELOCITY (Seed weight: 7%)
  {
    id: 'dim-execution-velocity-scored',
    name: 'Execution Velocity',
    dimension: 'execution-velocity',
    score: 76,
    maxScore: 100,
    feedback: 'Good execution speed. Consistently shipping product updates.',
    recommendation: 'Keep up the shipping velocity. Create a public changelog or release notes to showcase execution pace to investors.',
    riskLevel: 'low',
    evidenceLevel: 'partially-verified',
    verified: true,
    verificationNote: 'Product changelog and milestones reviewed',
    subScores: [
      { id: 'exec-speed', name: 'Execution Speed', score: 80, maxScore: 100, feedback: 'Shipping weekly, fast iteration cycles' },
      { id: 'exec-milestones', name: 'Milestone Achievement', score: 72, maxScore: 100, feedback: 'Generally hitting milestones, some feature delays' },
    ],
  },

  // 13. SCALABILITY (Seed weight: 4%)
  {
    id: 'dim-scalability-scored',
    name: 'Scalability',
    dimension: 'scalability',
    score: 64,
    maxScore: 100,
    feedback: 'Good technical foundation. Operational processes need work.',
    recommendation: 'Conduct load testing and document results. Automate key operational processes. Create hiring plan for next 12 months.',
    riskLevel: 'medium',
    evidenceLevel: 'partially-verified',
    verified: false,
    verificationNote: 'Architecture docs reviewed, no load testing yet',
    subScores: [
      { id: 'scale-tech', name: 'Technical Scalability', score: 72, maxScore: 100, feedback: 'Cloud-native architecture, can scale 10x' },
      { id: 'scale-ops', name: 'Operational Scalability', score: 55, maxScore: 100, feedback: 'Some manual processes, needs automation' },
      { id: 'scale-team', name: 'Team Scalability', score: 62, maxScore: 100, feedback: 'Good culture forming, hiring pipeline building' },
    ],
  },

  // 14. DEAL ATTRACTIVENESS (Seed weight: 3%)
  {
    id: 'dim-deal-attractiveness-scored',
    name: 'Deal Attractiveness',
    dimension: 'deal-attractiveness',
    score: 71,
    maxScore: 100,
    feedback: 'Reasonable valuation for seed stage. Clean cap table. SAFE terms standard.',
    recommendation: 'Prepare a clear use-of-funds breakdown for the next 18 months. Consider adding a strategic angel investor to the cap table for signal value.',
    riskLevel: 'low',
    evidenceLevel: 'partially-verified',
    verified: true,
    verificationNote: 'Cap table and term sheet reviewed',
    subScores: [
      { id: 'deal-valuation', name: 'Valuation Reasonableness', score: 70, maxScore: 100, feedback: '$8M cap on SAFE, reasonable for AI seed with $18K MRR' },
      { id: 'deal-terms', name: 'Deal Terms & Structure', score: 75, maxScore: 100, feedback: 'Standard SAFE with no unusual provisions' },
      { id: 'deal-use-of-funds', name: 'Use of Funds Clarity', score: 68, maxScore: 100, feedback: 'High-level plan shared, needs more granular breakdown' },
    ],
  },

  // 15. SOCIAL PROOF (Seed weight: 3%)
  {
    id: 'dim-social-proof-scored',
    name: 'Social Proof',
    dimension: 'social-proof',
    score: 60,
    maxScore: 100,
    feedback: 'Some notable advisors but limited institutional backing. Building press coverage.',
    recommendation: 'Add 1-2 well-known industry advisors. Apply to top-tier accelerators for credibility. Pursue press coverage in relevant trade publications.',
    riskLevel: 'low',
    evidenceLevel: 'unverified',
    verified: false,
    verificationNote: 'Advisor commitments self-reported, pending verification',
    subScores: [
      { id: 'social-advisors', name: 'Advisors & Mentors', score: 65, maxScore: 100, feedback: '2 advisors with relevant domain experience' },
      { id: 'social-backers', name: 'Notable Backers & Endorsements', score: 50, maxScore: 100, feedback: 'Pre-seed from friends/family, no institutional signal yet' },
      { id: 'social-press', name: 'Press & Recognition', score: 62, maxScore: 100, feedback: 'Featured in 1 industry blog, applied to TechCrunch Disrupt' },
    ],
  },
];

// Sample Data Room Documents
export const sampleDataRoomDocuments: DataRoomDocument[] = [
  {
    id: 'd1',
    name: 'TechFlow_AI_Pitch_Deck_2025.pdf',
    type: 'pitch_deck',
    uploadDate: '2025-01-10',
    size: '4.2 MB',
    status: 'reviewed',
    sharedWith: ['i1', 'i2'],
    comments: [
      { id: 'cm1', investorId: 'i1', investorName: 'Sarah Chen', text: 'Strong value proposition on slide 5. Would like to see more competitive analysis.', date: '2025-01-11', isPublic: true, commentedOnVersion: 1 },
    ],
    providerFileId: 'gd-1',
    classificationConfidence: 'high',
    typeOverridden: false,
    currentVersion: 2,
    versions: [
      { v: 1, uploadedAt: '2025-01-10T10:00:00Z', size: '3.9 MB', modifiedAt: '2025-01-10T10:00:00Z' },
      { v: 2, uploadedAt: '2026-04-15T14:00:00Z', size: '4.2 MB', modifiedAt: '2026-04-15T14:00:00Z' },
    ],
  },
  {
    id: 'd2',
    name: 'TechFlow_AI_Financial_Model_2025.xlsx',
    type: 'financials',
    uploadDate: '2025-01-08',
    size: '1.8 MB',
    status: 'reviewed',
    sharedWith: ['i1'],
    comments: [
      { id: 'cm2', investorId: 'i1', investorName: 'Sarah Chen', text: 'Revenue assumptions look aggressive for Q3. Can you provide supporting data?', date: '2025-01-12', isPublic: true, commentedOnVersion: 1 },
      { id: 'cm2b', investorId: 'i3', investorName: 'Elena Rodriguez', text: 'Unit economics look healthy — would love to see a cohort retention chart.', date: '2026-04-10', isPublic: true, commentedOnVersion: 1 },
    ],
    providerFileId: 'gd-2',
    classificationConfidence: 'high',
    typeOverridden: false,
    currentVersion: 1,
    versions: [
      { v: 1, uploadedAt: '2025-01-08T09:00:00Z', size: '1.8 MB', modifiedAt: '2025-01-08T09:00:00Z' },
    ],
  },
  {
    id: 'd3',
    name: 'TechFlow_AI_Team_Overview.pdf',
    type: 'team',
    uploadDate: '2025-01-05',
    size: '890 KB',
    status: 'reviewed',
    sharedWith: ['i1', 'i2', 'i3'],
    comments: [
      { id: 'cm_team1', investorId: 'i2', investorName: 'Marcus Williams', text: 'Founder backgrounds are strong. Any plan to hire a VP Eng?', date: '2026-03-18', isPublic: true, commentedOnVersion: 1 },
    ],
    providerFileId: 'gd-4',
    classificationConfidence: 'high',
    typeOverridden: false,
    currentVersion: 1,
    versions: [
      { v: 1, uploadedAt: '2025-01-05T10:00:00Z', size: '890 KB', modifiedAt: '2025-01-05T10:00:00Z' },
    ],
  },
  {
    id: 'd4',
    name: 'TechFlow_AI_DD_Memo.pdf',
    type: 'legal',
    uploadDate: '2025-01-03',
    size: '1.2 MB',
    status: 'reviewed',
    sharedWith: ['i1', 'i2'],
    comments: [
      { id: 'cm3', investorId: 'i1', investorName: 'Sarah Chen', text: 'Customer references look strong. Want to schedule calls with BrightPath and Summit Legal.', date: '2025-01-06', isPublic: true },
    ],
  },
  {
    id: 'd5',
    name: 'Cap Table.xlsx',
    type: 'legal',
    uploadDate: '2025-01-01',
    size: '256 KB',
    status: 'pending',
    sharedWith: [],
    comments: [],
  },
  {
    id: 'd6',
    name: 'Product Roadmap.pdf',
    type: 'product',
    uploadDate: '2024-12-28',
    size: '2.1 MB',
    status: 'reviewed',
    sharedWith: ['i1', 'i2'],
    comments: [],
  },
];

// Sample AI Recommendations
export const sampleAIRecommendations: AIRecommendation[] = [
  {
    id: 'r1',
    area: 'Partnerships & Pilots',
    priority: 'high',
    currentScore: 5,
    potentialScore: 8,
    suggestion: 'Establish 2-3 strategic partnerships with enterprise customers to validate market fit and improve credibility.',
    actionItems: [
      'Identify top 10 potential enterprise partners',
      'Develop partnership value proposition',
      'Create pilot program framework with success metrics',
      'Reach out to existing customer network for introductions',
    ],
  },
  {
    id: 'r2',
    area: 'Team Completeness',
    priority: 'high',
    currentScore: 6,
    potentialScore: 9,
    suggestion: 'Hire a dedicated sales/business development lead to accelerate customer acquisition.',
    actionItems: [
      'Define sales lead role and responsibilities',
      'Post on relevant job boards and leverage network',
      'Consider fractional/advisory sales help in interim',
    ],
  },
  {
    id: 'r3',
    area: 'Unit Economics',
    priority: 'medium',
    currentScore: 6,
    potentialScore: 8,
    suggestion: 'Implement better tracking of customer acquisition costs and lifetime value by cohort.',
    actionItems: [
      'Set up analytics to track CAC by channel',
      'Implement cohort analysis for LTV',
      'Create monthly unit economics dashboard',
    ],
  },
  {
    id: 'r4',
    area: 'Competitive Positioning',
    priority: 'medium',
    currentScore: 7,
    potentialScore: 9,
    suggestion: 'Develop clearer differentiation messaging against established competitors.',
    actionItems: [
      'Create detailed competitive battle cards',
      'Identify unique value propositions',
      'Update pitch deck with stronger positioning',
    ],
  },
];

// Sample Investor Matches
export const sampleInvestorMatches: InvestorMatch[] = [
  {
    id: 'm1',
    investor: mockInvestors[0],
    investorName: mockInvestorUsers[mockInvestors[0].id]?.name ?? 'Investor',
    matchScore: 94,
    matchReasons: ['Strong focus on Enterprise SaaS', 'AI/ML investment thesis', 'Seed stage preference', 'West Coast presence'],
    status: 'contacted',
  },
  {
    id: 'm2',
    investor: mockInvestors[2],
    investorName: mockInvestorUsers[mockInvestors[2].id]?.name ?? 'Investor',
    matchScore: 78,
    matchReasons: ['FinTech adjacent opportunity', 'Ticket size alignment', 'East Coast diversification interest'],
    status: 'new',
  },
  {
    id: 'm3',
    investor: mockInvestors[3],
    investorName: mockInvestorUsers[mockInvestors[3].id]?.name ?? 'Investor',
    matchScore: 65,
    matchReasons: ['Digital health crossover potential', 'Enterprise focus overlap'],
    status: 'new',
  },
];

// Sample Dealflow for Investors
// Helper to convert a flat mockStartup (StartupProfile) into the StartupSummary shape
// used by DealflowStartup and PortfolioCompany consumers.
function toStartupSummary(s: StartupProfile) {
  const enrichment = startupEnrichments[s.id];

  const base = {
    id: s.id,
    company: {
      name: s.name,
      tagline: s.tagline,
      description: s.description ?? '',
      industry: s.industry,
      stage: s.stage,
      location: s.location,
      founded: s.foundedYear,
      website: s.website ?? '',
    },
    funding: enrichment?.funding ?? {
      isActivelyRaising: s.isActivelyRaising ?? false,
      currentRaise: s.fundingTarget ? { amount: s.fundingTarget, currency: 'USD' } : null,
      history: [] as Array<{ roundType: string; amount: number; currency: string; status: string }>,
    },
    team: enrichment?.team ?? {
      size: s.teamSize,
      people: [
        ...(s.founderName ? [{ name: s.founderName, role: 'Founder', relationship: 'founder' as const, linkedIn: s.linkedIn }] : []),
      ],
      compositionSummary: '',
    },
    traction: enrichment?.traction ?? {
      metrics: [
        ...(s.monthlyRevenue ? [{ name: 'MRR', value: s.monthlyRevenue, unit: 'USD' }] : []),
        ...(s.userCount ? [{ name: 'Users', value: s.userCount, unit: 'count' }] : []),
        ...(s.growthRate ? [{ name: 'Growth', value: s.growthRate, unit: '%' }] : []),
      ],
    },
    market: enrichment?.market ?? {
      tam: s.marketSize ?? 0,
      sam: 0,
      som: 0,
      tamSource: '',
      growthRate: 0,
      competitors: (s.competitors ?? []).map(c => ({ name: c, relationship: 'competitor' })),
    },
    problemSolution: {
      problem: s.problem ?? '',
      solution: s.solution ?? '',
    },
    financials: enrichment?.financials ?? { unitEconomics: {} as Record<string, number | string | undefined>, projections: [] as Array<{ label: string; value: number; unit: string; period: string }> },
    product: enrichment?.product ?? ([] as Array<{ name: string; type: string }>),
    gtm: enrichment?.gtm ?? { channels: [] as string[], businessModel: '', pricingModel: '', distributionSummary: '' },
    socialProof: enrichment?.socialProof ?? { investorNames: [] as string[], partnerships: [] as string[], awards: [] as string[] },
    deckQuality: enrichment?.deckQuality ?? { structureScore: 0, narrativeFlow: '', designQuality: '', completeness: 0, missingSections: [] as string[] },
    // Pass through optional fields that remain at root
    targetCustomer: s.targetCustomer,
    isSerialFounder: s.isSerialFounder,
    previousVentures: s.previousVentures,
    founderEmail: s.founderEmail,
    updates: s.updates,
  };

  return base;
}

/**
 * The demo pipeline, one deal per position so every surface has something in it.
 *
 * `dataRoomAccess` used to be a field here and three of these four carried it
 * true while sitting at `screening`/`interested` — a combination the product can
 * no longer produce, because access IS `diligence` (only the founder's approval
 * enters it). The demo now says the same thing with the stage: df3 is waiting on
 * the founder with the documents shut, df4 has been let in.
 */
export const sampleDealflow: DealflowStartup[] = [
  {
    id: 'df1',
    startup: toStartupSummary(mockStartups[0]),
    readinessScore: 72,
    lastActivity: '2025-01-12',
    status: 'screening',
    notes: 'Strong team, need to validate unit economics',
  },
  {
    id: 'df2',
    startup: toStartupSummary(mockStartups[1]),
    readinessScore: 58,
    lastActivity: '2025-01-10',
    status: 'screening',
  },
  {
    id: 'df3',
    startup: toStartupSummary(mockStartups[2]),
    readinessScore: 81,
    lastActivity: '2025-01-08',
    status: 'awaiting_access',
    notes: 'Scheduling partner call for next week',
  },
  {
    id: 'df4',
    startup: toStartupSummary(mockStartups[3]),
    readinessScore: 89,
    lastActivity: '2025-01-05',
    status: 'diligence',
    notes: 'Series A ready, co-invest opportunity',
  },
];

// Sample Portfolio
export const samplePortfolio: PortfolioCompany[] = [
  {
    id: 'p1',
    startup: {
      id: 'ps1',
      company: {
        name: 'DataPulse',
        tagline: 'Real-time data analytics platform',
        description: '',
        industry: 'Enterprise SaaS',
        stage: 'series-a',
        location: 'Seattle, WA',
        founded: 2021,
        website: '',
      },
      funding: { isActivelyRaising: false, currentRaise: { amount: 5000000, currency: 'USD' }, history: [] },
      team: { size: 35, people: [], compositionSummary: '' },
      traction: { metrics: [] },
      market: { tam: 0, sam: 0, som: 0, tamSource: '', growthRate: 0, competitors: [] },
      problemSolution: { problem: '', solution: '' },
      financials: { unitEconomics: {}, projections: [] },
      product: [],
      gtm: { channels: [], businessModel: '', pricingModel: '', distributionSummary: '' },
      socialProof: { investorNames: [], partnerships: [], awards: [] },
      deckQuality: { structureScore: 0, narrativeFlow: '', designQuality: '', completeness: 0, missingSections: [] },
    },
    investmentDate: '2022-06-15',
    investmentAmount: 500000,
    equity: 4.2,
    currentValuation: 45000000,
    status: 'active',
    returns: 3.2,
    updates: [
      {
        id: 'u1',
        type: 'funding',
        title: 'Closed Series A',
        description: 'Closed Series A at $45M valuation',
        date: '2025-01-24',
      },
      {
        id: 'u2',
        type: 'team',
        title: 'New CTO Hired',
        description: 'Hired Sarah Johnson as CTO from Google',
        date: '2025-01-10',
      },
    ],
  },
  {
    id: 'p2',
    startup: {
      id: 'ps2',
      company: {
        name: 'CloudSecure',
        tagline: 'Zero-trust security for SMBs',
        description: '',
        industry: 'Cybersecurity',
        stage: 'series-a',
        location: 'Denver, CO',
        founded: 2020,
        website: '',
      },
      funding: { isActivelyRaising: false, currentRaise: { amount: 8000000, currency: 'USD' }, history: [] },
      team: { size: 42, people: [], compositionSummary: '' },
      traction: { metrics: [] },
      market: { tam: 0, sam: 0, som: 0, tamSource: '', growthRate: 0, competitors: [] },
      problemSolution: { problem: '', solution: '' },
      financials: { unitEconomics: {}, projections: [] },
      product: [],
      gtm: { channels: [], businessModel: '', pricingModel: '', distributionSummary: '' },
      socialProof: { investorNames: [], partnerships: [], awards: [] },
      deckQuality: { structureScore: 0, narrativeFlow: '', designQuality: '', completeness: 0, missingSections: [] },
    },
    investmentDate: '2021-03-20',
    investmentAmount: 250000,
    equity: 2.8,
    currentValuation: 62000000,
    status: 'active',
    returns: 5.8,
    updates: [
      {
        id: 'u3',
        type: 'traction',
        title: '$1M ARR Milestone',
        description: 'Hit $1M ARR milestone ahead of schedule',
        date: '2025-01-19',
      },
      {
        id: 'u4',
        type: 'product',
        title: 'SOC2 Certification',
        description: 'Achieved SOC2 Type II compliance',
        date: '2025-01-05',
      },
    ],
  },
  {
    id: 'p3',
    startup: {
      id: 'ps3',
      company: {
        name: 'MedConnect',
        tagline: 'Telehealth platform for specialists',
        description: '',
        industry: 'HealthTech',
        stage: 'seed',
        location: 'Chicago, IL',
        founded: 2023,
        website: '',
      },
      funding: { isActivelyRaising: false, currentRaise: { amount: 2000000, currency: 'USD' }, history: [] },
      team: { size: 15, people: [], compositionSummary: '' },
      traction: { metrics: [] },
      market: { tam: 0, sam: 0, som: 0, tamSource: '', growthRate: 0, competitors: [] },
      problemSolution: { problem: '', solution: '' },
      financials: { unitEconomics: {}, projections: [] },
      product: [],
      gtm: { channels: [], businessModel: '', pricingModel: '', distributionSummary: '' },
      socialProof: { investorNames: [], partnerships: [], awards: [] },
      deckQuality: { structureScore: 0, narrativeFlow: '', designQuality: '', completeness: 0, missingSections: [] },
    },
    investmentDate: '2024-01-10',
    investmentAmount: 150000,
    equity: 3.5,
    currentValuation: 8000000,
    status: 'active',
    returns: 1.4,
    updates: [
      {
        id: 'u5',
        type: 'traction',
        title: 'New Enterprise Clients',
        description: 'Added 3 new enterprise hospital clients',
        date: '2025-01-12',
      },
      {
        id: 'u6',
        type: 'milestone',
        title: 'FDA Clearance',
        description: 'Received FDA 510(k) clearance for remote monitoring',
        date: '2024-12-20',
      },
    ],
  },
];

// Sample Verification Requests (for startups to verify investor claims)
export const sampleVerificationRequests: VerificationRequest[] = [
  {
    id: 'vr1',
    investorId: 'i1',
    investorName: 'Sarah Chen',
    investorFirm: 'Horizon Ventures',
    claimId: 'claim1',
    requestDate: '2026-01-12T10:00:00Z',
    status: 'pending',
  },
  {
    id: 'vr2',
    investorId: 'i2',
    investorName: 'Marcus Williams',
    investorFirm: 'Green Capital',
    claimId: 'claim2',
    requestDate: '2026-01-10T14:30:00Z',
    status: 'pending',
  },
  {
    id: 'vr3',
    investorId: 'i3',
    investorName: 'Elena Rodriguez',
    investorFirm: 'FinTech Angels',
    claimId: 'claim3',
    requestDate: '2026-01-05T09:15:00Z',
    status: 'verified',
    respondedAt: '2026-01-06T11:00:00Z',
  },
];

// Sample Investment Commitments (verified — from on-platform investors)
export const sampleCommitments: InvestorCommitment[] = [
  // s1 (TechFlow AI) — $500K hard + $200K soft = $700K verified
  {
    id: 'vc1',
    investorId: 'i1',
    investorName: 'Sarah Chen',
    investorFirm: 'Horizon Ventures',
    startupId: 's1',
    type: 'hard',
    source: 'verified',
    amount: 500000,
    status: 'converted',
    createdAt: '2026-01-10T10:00:00Z',
    updatedAt: '2026-01-28T14:00:00Z',
    convertedAt: '2026-01-28T14:00:00Z',
    notes: 'Lead check — closing after board review.',
  },
  {
    id: 'vc2',
    investorId: 'i3',
    investorName: 'Elena Rodriguez',
    investorFirm: 'FinTech Angels',
    startupId: 's1',
    type: 'soft',
    source: 'verified',
    amount: 200000,
    status: 'active',
    createdAt: '2026-02-01T09:00:00Z',
    updatedAt: '2026-02-01T09:00:00Z',
    notes: 'Interested pending term sheet review.',
  },
  // s3 (FinLedger) — $750K hard verified
  {
    id: 'vc3',
    investorId: 'i1',
    investorName: 'Sarah Chen',
    investorFirm: 'Horizon Ventures',
    startupId: 's3',
    type: 'hard',
    source: 'verified',
    amount: 750000,
    status: 'converted',
    createdAt: '2025-12-15T11:00:00Z',
    updatedAt: '2026-01-05T16:00:00Z',
    convertedAt: '2026-01-05T16:00:00Z',
  },
  // Withdrawn example (for metrics) — i2 withdrew from s1
  {
    id: 'vc4',
    investorId: 'i2',
    investorName: 'Marcus Williams',
    investorFirm: 'Green Capital',
    startupId: 's1',
    type: 'soft',
    source: 'verified',
    amount: 150000,
    status: 'withdrawn',
    createdAt: '2026-01-05T08:00:00Z',
    updatedAt: '2026-01-20T10:00:00Z',
    withdrawnAt: '2026-01-20T10:00:00Z',
    notes: 'Decided to pass after deeper review.',
  },
  // i4 soft commit on s1
  {
    id: 'vc5',
    investorId: 'i4',
    investorName: 'David Park',
    investorFirm: 'Health Invest Capital',
    startupId: 's1',
    type: 'soft',
    source: 'verified',
    amount: 100000,
    status: 'active',
    createdAt: '2026-02-05T13:00:00Z',
    updatedAt: '2026-02-05T13:00:00Z',
  },
];

// Sample External Commitments (unverified — reported by startups)
export const sampleExternalCommitments: ExternalCommitment[] = [
  // s1 (TechFlow AI) — $250K hard + $100K soft external = $350K unverified
  {
    id: 'ec1',
    startupId: 's1',
    investorName: 'James O\'Brien',
    investorFirm: 'Alpine Angels',
    type: 'hard',
    source: 'unverified',
    amount: 250000,
    status: 'active',
    createdAt: '2026-01-18T12:00:00Z',
    updatedAt: '2026-01-18T12:00:00Z',
    notes: 'Verbal commitment at Demo Day.',
  },
  {
    id: 'ec2',
    startupId: 's1',
    investorName: 'Priya Sharma',
    investorFirm: 'Latticework Fund',
    type: 'soft',
    source: 'unverified',
    amount: 100000,
    status: 'active',
    createdAt: '2026-02-03T15:00:00Z',
    updatedAt: '2026-02-03T15:00:00Z',
  },
  // s3 (FinLedger) — $500K hard external
  {
    id: 'ec3',
    startupId: 's3',
    investorName: 'Bank Innovation Fund',
    investorFirm: 'BIF Partners',
    type: 'hard',
    source: 'unverified',
    amount: 500000,
    status: 'active',
    createdAt: '2026-01-22T10:00:00Z',
    updatedAt: '2026-01-22T10:00:00Z',
    notes: 'Strategic investor — banking sector.',
  },
];

// Sample Data Room Access Requests (investor → startup access requests for DD)
export interface SampleAccessRequest {
  id: string;
  startupId: string;
  investorId: string;
  investorName: string;
  investorFirm?: string;
  message?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
  respondedAt?: string;
}

export const sampleAccessRequests: SampleAccessRequest[] = [
  // s1 — pending
  {
    id: 'ar1',
    startupId: 's1',
    investorId: 'i4',
    investorName: 'David Park',
    investorFirm: 'Health Invest Capital',
    message: 'We follow vertical SaaS closely and would love to dig into your retention metrics and pipeline.',
    status: 'pending',
    createdAt: '2026-04-26T09:30:00Z',
  },
  {
    id: 'ar2',
    startupId: 's1',
    investorId: 'i5',
    investorName: 'Priya Sharma',
    investorFirm: 'Latticework Fund',
    message: 'Met your team at the Demo Day — keen to review the financial model.',
    status: 'pending',
    createdAt: '2026-04-28T14:10:00Z',
  },
  // s1 — granted
  {
    id: 'ar3',
    startupId: 's1',
    investorId: 'i1',
    investorName: 'Sarah Chen',
    investorFirm: 'Horizon Ventures',
    status: 'approved',
    createdAt: '2026-01-08T08:00:00Z',
    respondedAt: '2026-01-09T10:30:00Z',
  },
  {
    id: 'ar4',
    startupId: 's1',
    investorId: 'i3',
    investorName: 'Elena Rodriguez',
    investorFirm: 'FinTech Angels',
    status: 'approved',
    createdAt: '2026-01-30T11:20:00Z',
    respondedAt: '2026-02-01T09:00:00Z',
  },
  // s1 — revoked
  {
    id: 'ar5',
    startupId: 's1',
    investorId: 'i2',
    investorName: 'Marcus Williams',
    investorFirm: 'Green Capital',
    message: 'Looking at industrial AI plays this quarter.',
    status: 'rejected',
    createdAt: '2026-01-04T13:00:00Z',
    respondedAt: '2026-01-20T10:00:00Z',
  },
];

/**
 * Get the default startup profile (TechFlow AI) for testing
 * Returns a complete profile with all fields populated for realistic score calculations
 */
export const getDefaultStartupProfile = (): StartupProfile => mockStartups[0];
